// PaymentSuccess.jsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase';
import { PLAN_CAPS, PLAN_CAPS_ANNUAL } from '../services/usageService';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Activating your account...');
  const [error, setError] = useState('');
  
  useEffect(() => {
    const auth = getAuth();
    
    // Wait for auth to be ready
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is logged in - activate their subscription!
        await activateSubscription(user.uid);
      } else {
        // No user logged in - check session storage as fallback
        const pendingUserId = sessionStorage.getItem('pendingUserId');
        if (pendingUserId) {
          await activateSubscription(pendingUserId);
        } else {
          setError('Please log in to complete your subscription.');
        }
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);
  
  const activateSubscription = async (userId) => {
    try {
      if (!userId) {
        setError('Unable to verify user. Please contact support.');
        return;
      }
      
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const pendingPlan = userData.pendingPlan;
        
        if (!pendingPlan) {
          // Check if already active
          if (userData.subscriptionStatus === 'active' || userData.subscriptionStatus === 'trialing') {
            setStatus('Your subscription is already active! Redirecting to dashboard...');
            setTimeout(() => navigate('/dashboard'), 2000);
            return;
          }
          
          setError('No pending plan found. Please select a plan first.');
          return;
        }
        
        // Get plan caps from usageService
        const planType = pendingPlan.planType || 'starter';
        const billingCycle = pendingPlan.billingCycle || 'monthly';
        let caps;
        if (billingCycle === 'annual' || billingCycle === 'yearly') {
          caps = PLAN_CAPS_ANNUAL[planType] || PLAN_CAPS[planType] || PLAN_CAPS.starter;
        } else {
          caps = PLAN_CAPS[planType] || PLAN_CAPS.starter;
        }
        
        await setDoc(userRef, {
          subscriptionStatus: billingCycle === 'trial' ? 'trialing' : 'active',
          subscriptionPlan: planType,
          billingCycle: billingCycle,
          pendingPlan: null,
          subscriptionActivatedAt: serverTimestamp(),
          trialEndsAt: billingCycle === 'trial' 
            ? new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) 
            : null,
          updatedAt: serverTimestamp(),
          
          // ---- New usage & plan caps fields ----
          usage: {
            imagesGenerated: 0,
            videosGenerated: 0,
            lastUpdatedAt: serverTimestamp(),
          },
          planCaps: {
            imageCap: caps.imageCap,
            videoCap: caps.videoCap,
            genBudget: caps.genBudget,
          },
          // Keep the old field for backward compatibility if needed
          subscription: {
            planName: planType,
            billingCycle: billingCycle,
            status: billingCycle === 'trial' ? 'trialing' : 'active',
            updatedAt: new Date().toISOString(),
          }
        }, { merge: true });
        
        sessionStorage.removeItem('pendingUserId');
        sessionStorage.removeItem('selectedPlan');
        
        setStatus('Payment successful! Redirecting to dashboard...');
        
        // ✅ Send message to parent (if inside iframe)
        if (window.parent !== window) {
          window.parent.postMessage('payment_success', '*');
        }
        
        setTimeout(() => {
          if (window.opener) {
            window.opener.location.href = '/dashboard';
            window.close();
          } else {
            navigate('/dashboard');
          }
        }, 2000);
      } else {
        setError('User account not found. Please contact support.');
      }
    } catch (error) {
      console.error('Error activating subscription:', error);
      setError('Error activating account. Please contact support.');
    }
  };
  
  return (
    <>
      <style>
        {`
          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            min-height: 100vh;
            background-color: #FAFAFA;
          }
          
          #root {
            width: 100%;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        minHeight: '100vh',
        backgroundColor: '#FAFAFA',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'avenir-lt-pro, sans-serif',
        padding: '20px',
        boxSizing: 'border-box',
        margin: 0,
        overflow: 'auto'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '450px',
          margin: '0 auto',
          padding: '40px 30px',
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          textAlign: 'center',
          boxSizing: 'border-box'
        }}>
          {error ? (
            <>
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                backgroundColor: '#E53935',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 25px'
              }}>
                <span style={{ color: 'white', fontSize: '36px', fontWeight: '300' }}>✕</span>
              </div>
              
              <h2 style={{
                color: '#333333',
                fontSize: 'clamp(1.3rem, 5vw, 1.6rem)',
                marginBottom: '15px',
                fontWeight: '500',
                lineHeight: '1.3'
              }}>
                Something went wrong
              </h2>
              
              <p style={{
                color: '#666666',
                fontSize: 'clamp(0.9rem, 4vw, 1rem)',
                marginBottom: '30px',
                lineHeight: '1.5',
                padding: '0 10px'
              }}>
                {error}
              </p>
              
              <button
                onClick={() => navigate('/')}
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  padding: '14px 30px',
                  backgroundColor: '#88B7FF',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '30px',
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  margin: '0 auto'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#5998FF'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#88B7FF'}
              >
                Return to Home
              </button>
            </>
          ) : (
            <>
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                backgroundColor: '#4CAF50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 25px'
              }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              
              <h2 style={{
                color: '#333333',
                fontSize: 'clamp(1.3rem, 5vw, 1.6rem)',
                marginBottom: '15px',
                fontWeight: '500',
                lineHeight: '1.3'
              }}>
                {status}
              </h2>
              
              <div style={{
                width: '45px',
                height: '45px',
                border: '3px solid #EEEEEE',
                borderTop: '3px solid #88B7FF',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '10px auto 0'
              }} />
            </>
          )}
        </div>
      </div>
    </>
  );
}